<template>
    <div class="tw:flex tw:flex-col tw:gap-4 tw:pb-4">
        <!-- Loading -->
        <CardFront v-if="loadingAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-8 tw:flex tw:items-center tw:justify-center tw:min-h-[300px]">
                <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                    <div class="tw:w-8 tw:h-8 tw:border-2 tw:border-gray-300 tw:border-t-blue-500 tw:rounded-full tw:animate-spin"></div>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        {{ $t('perfil.cambio_butaca.cargando_mapa') }}
                    </p>
                </div>
            </div>
        </CardFront>

        <!-- Error -->
        <CardFront v-else-if="errorAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-6">
                <div class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm">
                    {{ errorAsientos }}
                </div>
            </div>
        </CardFront>

        <!-- ═══════════════════════════════════════════ -->
        <!-- CAMINO Leaflet 3D: tipo_svg=2 OR tipo_svg=1 + secciones -->
        <!-- (FIX del bug de master: la condición correcta es Leaflet      -->
        <!--  cuando tipoSvg===2, o tipoSvg===1 con tipo_proceso_online===2 -->
        <!--  que es el caso de regiones anexas con secciones y plazas.)   -->
        <!-- ═══════════════════════════════════════════ -->
        <template v-else-if="tipoSvg === 2 || (tipoSvg === 1 && tipoProcesoOnline === 2)">
            <!-- SVG 3D seat map -->
            <template v-if="svgParsed">
                <CardFront custom-classes="tw:px-0 tw:py-0 tw:overflow-hidden">
                    <LeafletComponent
                        :svg-rendered="svgParsed"
                        :plazas-seleccionadas="plazasSeleccionadas"
                        :original-color="COLOR_LIBRE"
                        :disabled="false"
                        @is-panning="onIsPanning"
                    />
                </CardFront>

                <!-- Selected seat card -->
                <CardFront v-if="asientoSeleccionado" custom-classes="tw:px-0 tw:py-0">
                    <div class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3">
                        <div class="tw:flex tw:items-center tw:gap-3">
                            <div class="tw:w-9 tw:h-9 tw:rounded-full tw:bg-blue-900/20 tw:flex tw:items-center tw:justify-center">
                                <i class="material-icons tw:text-blue-900 tw:text-xl">event_seat</i>
                            </div>
                            <div>
                                <p class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-blue-900 tw:mb-0.5">
                                    {{ $t('perfil.cambio_butaca.butaca_seleccionada') }}
                                </p>
                                <p class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:mb-0">
                                    {{ zoneName }} — {{ $t('perfil.cambio_butaca.fila') }} {{ asientoSeleccionado.row || asientoSeleccionado.fila }} —
                                    {{ $t('perfil.cambio_butaca.butaca') }} {{ asientoSeleccionado.number || asientoSeleccionado.asiento }}
                                </p>
                            </div>
                        </div>
                        <button
                            class="tw:text-sm tw:text-gray-400 hover:tw:text-red-500 tw:transition-colors tw:cursor-pointer tw:border-0 tw:bg-transparent"
                            :title="$t('perfil.cambio_butaca.quitar_butaca')"
                            @click="deselectSeat"
                        >
                            <i class="material-icons tw:text-lg">close</i>
                        </button>
                    </div>

                    <!-- Tipo de abono selector (solo si múltiples opciones) -->
                    <template v-if="asientoSeleccionado && preciosDisponibles.length > 1">
                        <div class="tw:px-4 tw:pb-3">
                            <label class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1 tw:block">
                                {{ $t('perfil.cambio_butaca.seleccionar_tipo_abono') }}
                            </label>
                            <select
                                class="tw:w-full tw:border tw:border-gray-200 tw:rounded-lg tw:px-3 tw:py-2 tw:text-sm tw:focus:border-blue-400 tw:focus:ring-1 tw:focus:ring-blue-400 tw:outline-none"
                                :value="tipoAbonoSeatId"
                                @change="$emit('update:tipo-abono-seat-id', Number($event.target.value))"
                            >
                                <option :value="null" disabled>{{ $t('perfil.cambio_butaca.seleccionar_tipo_abono') }}</option>
                                <option
                                    v-for="precio in preciosDisponibles"
                                    :key="precio.id"
                                    :value="precio.id"
                                >
                                    {{ precio.descripcion }}
                                </option>
                            </select>
                        </div>
                    </template>
                </CardFront>

                <!-- Legend -->
                <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:px-1">
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0CBA31] tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_butaca.disponible') }}
                        </span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#CC0000] tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_butaca.ocupado') }}
                        </span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0F256E] tw:ring-2 tw:ring-blue-300 tw:flex-shrink-0"></span>
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                            {{ $t('perfil.cambio_butaca.seleccionado') }}
                        </span>
                    </div>
                </div>
            </template>

            <!-- Loading: SVG recibido pero aún parseándose (evita flash de empty state) -->
            <CardFront v-else-if="svgPreparando" custom-classes="tw:px-0 tw:py-0">
                <div class="tw:p-8 tw:flex tw:items-center tw:justify-center tw:min-h-[300px]">
                    <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                        <div class="tw:w-8 tw:h-8 tw:border-2 tw:border-gray-300 tw:border-t-blue-500 tw:rounded-full tw:animate-spin"></div>
                        <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                            {{ $t('perfil.cambio_butaca.cargando_mapa') }}
                        </p>
                    </div>
                </div>
            </CardFront>

            <!-- Empty state (no SVG parsed) -->
            <CardFront v-else custom-classes="tw:px-0 tw:py-0">
                <div class="tw:p-6 tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
                    <i class="material-icons tw:text-3xl tw:text-gray-300">event_busy</i>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        {{ $t('perfil.cambio_butaca.sin_butacas_disponibles') }}
                    </p>
                </div>
            </CardFront>
        </template>

        <!-- ═══════════════════════════════════════════ -->
        <!-- CAMINO antifluo (v-else): SeatPicker grid  -->
        <!-- Se renderiza cuando NO aplica el path Leaflet  -->
        <!-- de arriba (`tipoSvg===2` o `tipoSvg===1 &&   -->
        <!-- `tipoProcesoOnline===2`). Cubre tipoSvg!==2  -->
        <!-- sin secciones, tipoSvg===1+proceso===1 sin    -->
        <!-- plazas, y tipoSvg===null (fallback).          -->
        <!-- ═══════════════════════════════════════════ -->
        <template v-else>
            <SeatSelectionCard
                :seats="seatsForPicker"
                :loading="false"
                :error="null"
                :selected-seats="selectedSeatsForPicker"
                :max-seats="1"
                :zone-name="zoneName"
                :sidebar-title="$t('perfil.cambio_butaca.butaca_seleccionada')"
                :empty-label="$t('perfil.cambio_butaca.sin_butacas_seleccionadas')"
                :row-label="$t('perfil.cambio_butaca.fila')"
                :seat-label="$t('perfil.cambio_butaca.butaca')"
                :map-hint-label="$t('perfil.cambio_butaca.selecciona_butaca')"
                @toggle-asiento="onSeatPickerToggle"
                @quitar-asiento="() => $emit('quitar-asiento')"
            />

            <!-- Legend -->
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:px-1">
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0CBA31] tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_butaca.disponible') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#CC0000] tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_butaca.ocupado') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0F256E] tw:ring-2 tw:ring-blue-300 tw:flex-shrink-0"></span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                        {{ $t('perfil.cambio_butaca.seleccionado') }}
                    </span>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import LeafletComponent from '@/components/svg/components/LeafletComponent.vue';
    import SeatSelectionCard from '@/modules/front/ticketing/SeatSelectionCard.vue';
    import { useSvgSeatMap } from '@/composables/front/perfil/useSvgSeatMap';

    // ─── Props ──────────────────────────────────────────────────────────────
    const props = defineProps({
        tipoSvg:                 { type: Number,  default: null },
        tipoProcesoOnline:       { type: Number,  default: null },
        asientosData:            { type: Object,  default: null },
        svgString:               { type: String,  default: '' },
        loadingAsientos:         { type: Boolean, default: false },
        errorAsientos:           { type: [String, Boolean], default: null },
        asientoSeleccionado:     { type: Object,  default: null },
        zonaSeleccionada:        { type: Object,  default: null },
        preciosDisponibles:      { type: Array,   default: () => [] },
        tipoAbonoSeatId:         { type: [Number, null], default: null },
        /** Asientos normalizados para SeatPicker (camino antifluo del v-else:
         *  se usa cuando NO aplica el path Leaflet — i.e. cuando `tipoSvg !== 2`
         *  y NO es `tipoSvg===1 && tipoProcesoOnline===2` (secciones con plazas)).
         *
         *  El orquestador (`CambioButacaView`) SIEMPRE debe pasar este prop
         *  desde `asientosParaPicker` del composable. El default `[]` existe
         *  como salvaguarda para tests unitarios y para evitar un render roto
         *  si el caller olvidara el binding — pero la rama antifluo con `[]`
         *  mostraría el grid de butacas vacío, que NO es el comportamiento
         *  deseado en producción. Ver CambioButacaView.vue:122. */
        seatsForPicker:          { type: Array,   default: () => [] },
    });

    const emit = defineEmits(['toggle-asiento', 'quitar-asiento', 'update:tipo-abono-seat-id']);

    // ─── SVG 3D seat map (tipo_svg=1) ────────────────────────────────────
    const {
        svgParsed,
        svgPreparando,
        onIsPanning,
        deselectSeat,
        COLOR_LIBRE,
    } = useSvgSeatMap(props, emit);

    // ─── Zone / seat display ──────────────────────────────────────────────

    const zoneName = computed(() =>
        props.zonaSeleccionada?.name ?? props.zonaSeleccionada?.nombre ?? ''
    );

    /**
     * Array con la butaca seleccionada (o vacío) para SeatSelectionCard.
     */
    const selectedSeatsForPicker = computed(() =>
        props.asientoSeleccionado ? [props.asientoSeleccionado] : []
    );

    /**
     * Array con la butaca seleccionada (o vacío) para LeafletComponent.
     *
     * Contrato de la prop `plazasSeleccionadas` de LeafletComponent: array de
     * objetos con `id` (identidad para el diff de su watch), `svg_id` (localiza
     * el círculo con `[id^="butaca-${svg_id}-"]`) y `precios` (si `length > 0`
     * restaura `originalColor`, si no `gray`). Pasar un `[]` constante rompe la
     * cadena: su watch sobre `clonedPlazasSeleccionadas` nunca dispara y la
     * butaca queda marcada al quitar desde la X. Este computed replica el
     * patrón legacy de `SeleccionarAsientosMapaGeneral` /
     * `SeleccionarAsientosRegionAnexa`: la X emite `quitar-asiento`, el padre
     * null-ifica `asientoSeleccionado` y este computed vuelve a `[]`, con lo
     * que Leaflet repinta (desmarca).
     */
    const plazasSeleccionadas = computed(() =>
        props.asientoSeleccionado ? [props.asientoSeleccionado] : []
    );

    // ─── SeatPicker interaction (tipo_svg=2) ─────────────────────────────

    function onSeatPickerToggle(seat) {
        emit('toggle-asiento', seat);
    }
</script>
